import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import type { AppDispatch, RootState } from "../../redux/store";

import {
  listenToPublishedProducts,
  filterLocalProductsByCountry,
  setFilters,
} from "../../redux/productSlice";
import { addToCart, decrementCartItem, clearCart } from "../../redux/cartSlice";
import { listenToBrandsByBusinessType } from "../../redux/commercePartnersSlice";
import { fetchAllProductSummaries } from "../../redux/productRatingSlice";
import { CurrencyContext } from "../../GLOBAL/CurrencyContext";
import appLogo from "../../assets/app/logo.png";

/* ═══════════════════════════════════════════════════════════
   CATEGORY SOURCE — strictly idBooksCategories, nothing else
═══════════════════════════════════════════════════════════ */
import {
  idBooksCategories,
  idBooksGenreFiction,
  idBooksNonFictionSubcategories,
  idBooksChildrensSubcategories,
  idBooksYASubcategories,
  idBooksAcademicSubcategories,
  idBooksComicsSubcategories,
  idBooksArtsSubcategories,
  idBooksTravelSubcategories,
  idBooksReligionSubcategories,
  idBooksLanguagesSubcategories,
  type CategoryItem,
} from "../../CATEGORIES/BooksCategories";

/* ═══════════════════════════════════════════════════════════
   BUSINESS / STORE TYPE — pulled directly from the shared
   PartnerCategories registry (businessType / storeType), not
   hardcoded, so Books stays in sync with that source of truth —
   exactly the same pattern Bags / Kitchen / Farm / Automotive use.
   Confirmed ids: businessType "Stores" = id 12, storeType
   "Books Store" = id 1222.
═══════════════════════════════════════════════════════════ */
import { businessType, storeType } from "../../CATEGORIES/PartnerCategories";

import {
  Search, ShoppingBag, Menu, X, ChevronRight, Plus, Minus,
  Heart, User, ShoppingCart, MapPin, Globe, AlertTriangle, ArrowRight,
  BookOpen, Home, ShoppingBasket, Star, ChevronDown, Tag, Check,
} from "lucide-react";

/* ═══════════════════════════════════════════════════════════
   sphere helper — used for the colour-swatch variant picker
═══════════════════════════════════════════════════════════ */
function sphere3D(hex: string): string {
  return [
    `radial-gradient(circle at 35% 32%, rgba(255,255,255,0.6) 0%, rgba(255,255,255,0.12) 28%, transparent 52%),`,
    `radial-gradient(circle at 68% 72%, rgba(0,0,0,0.28) 0%, transparent 55%),`,
    `radial-gradient(circle at 50% 50%, ${hex} 0%, ${hex} 100%)`,
  ].join(" ");
}

/* ═══════════════════════════════════════════════════════════
   DESIGN TOKENS — Books-focused palette
   (deep navy, warm cream, gold accents — literary aesthetic)
═══════════════════════════════════════════════════════════ */
const T = {
  pageBg:   "#FAF7F2",
  cardBg:   "#FFFFFF",
  headerBg: "#1A3A52",

  black:    "#1B0E06",
  white:    "#FFFFFF",
  offwhite: "#F5EFE7",
  muted:    "#6B5B4F",
  mutedDim: "#9A8B7E",

  orange:   "#D4843C", // warm gold for books
  red:      "#8B2E1F", // deep burgundy
  redDim:   "#FDEAE3",

  blue:     "#2C5F8D", // navy (primary accent for books)
  blueDim:  "rgba(44,95,141,0.10)",

  cream:    "#F5EFE7",
  peach:    "#E8D9C8",

  border:       "#E8DFD3",
  borderStrong: "rgba(27,14,6,0.22)",

  grad:  "linear-gradient(135deg,#1A3A52 0%,#2C5F8D 100%)",
  gradH: "linear-gradient(90deg,#D4843C 0%,#8B2E1F 100%)",

  outline: "1.5px solid #E8DFD3",
};

const GRAD   = T.grad;
const GRAD_H = T.gradH;

const RGBA_DARK = (a: number) => `rgba(27,14,6,${a})`;
const RGBA_GOLD = (a: number) => `rgba(212,132,60,${a})`;
const RGBA_RED  = (a: number) => `rgba(139,46,31,${a})`;

/* books accent rotation — navy / gold / burgundy */
const PRODUCT_ACCENT_COLORS = [T.blue, T.orange, T.red];
const productAccentColor = (key: string) => {
  let hash = 0;
  for (let i = 0; i < key.length; i++) hash = (hash * 31 + key.charCodeAt(i)) >>> 0;
  return PRODUCT_ACCENT_COLORS[hash % PRODUCT_ACCENT_COLORS.length];
};

/* ═══════════════════════════════════════════════════════════
   CATEGORY REGISTRY — strictly idBooksCategories
   Each top-level category maps to its own subcategory list,
   derived directly from CAT_PRODUCTS' structure — no manual
   per-category array to keep in sync. Same one-layer shape
   Bags/Kitchen/Farm/Automotive use for CAT_SUBCATS.
═══════════════════════════════════════════════════════════ */
const ALL_TOP_CATS: CategoryItem[] = idBooksCategories;

const CAT_SUBCATS: Record<number, CategoryItem[]> = {
  301: idBooksGenreFiction,
  302: idBooksNonFictionSubcategories,
  303: idBooksChildrensSubcategories,
  304: idBooksYASubcategories,
  305: idBooksAcademicSubcategories,
  306: idBooksComicsSubcategories,
  307: idBooksArtsSubcategories,
  308: idBooksTravelSubcategories,
  309: idBooksReligionSubcategories,
  310: idBooksLanguagesSubcategories,
};

/* ═══════════════════════════════════════════════════════════
   BUSINESS / STORE TYPE — Books isn't its own top-level business
   type. It lives under business type "Stores" (id 12), store
   type "Books Store" (id 1222) — resolved from the shared
   PartnerCategories registry rather than hardcoded, so every
   partner query on this page — local AND global — matches BOTH
   ids, or the grid will pull in non-Books-Store brands. Same
   exact pattern as Bags ("Luggage Store") / Kitchen ("Kitchen Store")
   / Farm / Automotive.
═══════════════════════════════════════════════════════════ */
const BUSINESS_TYPE_ID = businessType.find((b) => b.id === 12)?.id ?? 12;
const STORE_TYPE_ID    = storeType.find((s) => s.id === 1222)?.id ?? 1222;

const COUNTRY_TO_CONTINENT: Record<string, string> = {
  estonia: "Europe", latvia: "Europe", lithuania: "Europe", finland: "Europe",
  sweden: "Europe", norway: "Europe", denmark: "Europe", germany: "Europe",
  france: "Europe", spain: "Europe", italy: "Europe", portugal: "Europe",
  poland: "Europe", "united kingdom": "Europe", ireland: "Europe",
  netherlands: "Europe", belgium: "Europe", austria: "Europe",
  switzerland: "Europe", greece: "Europe",
  "united states": "North America", canada: "North America", mexico: "North America",
  brazil: "South America", argentina: "South America",
  nigeria: "Africa", kenya: "Africa", "south africa": "Africa",
  china: "Asia", japan: "Asia", india: "Asia", "united arab emirates": "Asia",
  australia: "Oceania", "new zealand": "Oceania",
};
const getContinent = (country?: string | null): string | null => {
  if (!country) return null;
  return COUNTRY_TO_CONTINENT[country.toLowerCase().trim()] ?? null;
};

/* tier 0 = same city, 1 = same country, 2 = same continent, 3 = rest of world */
const isLocalTier = (tier: number) => tier <= 1;
const isGlobalTier = (tier: number) => tier >= 2;

/* ═══════════════════════════════════════════════════════════
   GLOBAL CSS — Literary aesthetic
═══════════════════════════════════════════════════════════ */
const GLOBAL_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Merriweather:wght@400;700;900&family=Inter:wght@300;400;500;600;700;800;900&display=swap');
*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
::-webkit-scrollbar { display: none; }
input, button { font-family: inherit; }
input::placeholder { color: rgba(255,255,255,.55); }

@keyframes gradSpin { 0%{background-position:0% 50%;} 50%{background-position:100% 50%;} 100%{background-position:0% 50%;} }
@keyframes mc-pulse   { 0%,100%{opacity:1} 50%{opacity:.35} }

.p-card { transition: box-shadow .22s, transform .22s; }
.p-card:hover { box-shadow: 0 0 0 1.5px ${RGBA_RED(0.30)}, 0 14px 36px ${RGBA_GOLD(0.18)}; transform: translateY(-6px); }

.add-btn-3d { transition: transform .18s ease, box-shadow .18s ease; }
.add-btn-3d:hover  { transform: translateY(-3px); box-shadow: 0 10px 24px var(--shadow) !important; }
.add-btn-3d:active { transform: translateY(0) scale(.96); box-shadow: 0 4px 14px var(--shadow) !important; }

.h-btn{
  display:flex;align-items:center;gap:6px;
  background:rgba(255,255,255,.16);
  border:1px solid rgba(255,255,255,.28);
  cursor:pointer;color:#fff;
  font-size:12px;font-weight:700;
  padding:7px 13px;border-radius:8px;
  flex-shrink:0;white-space:nowrap;
  transition:background .2s;
  font-family:inherit;
}
.h-btn:hover{background:rgba(255,255,255,.26);}

.nav-item { display:flex; align-items:center; gap:12px; padding:13px 18px; cursor:pointer; font-size:14px; font-weight:700; color:#F5EFE7; background:none; width:100%; text-align:left; border:none; border-bottom:1px solid rgba(255,255,255,.14); transition:background .15s; }
.nav-item:hover { background: rgba(255,255,255,.10); }

.tab-item { display:flex; flex-direction:column; align-items:center; gap:3px; font-size:10px; font-weight:700; cursor:pointer; color:#9A8B7E; padding:4px 10px; transition:color .2s; }
.tab-item.active { color:#8B2E1F; }

.tag-label { font-family:'Merriweather',serif; letter-spacing:0.08em; }
`;

/* ═══════════════════════════════════════════════════════════
   SIDEBAR STYLES
═══════════════════════════════════════════════════════════ */
const A: Record<string, React.CSSProperties> = {
  commerceCard:   { borderRadius: 14, padding: 18, background: "#fff", marginBottom: 16, boxShadow: `0 2px 16px ${RGBA_DARK(0.06)}`, border: T.outline },
  commerceHeader: { fontSize: 16, fontWeight: 900, display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer", color: T.black },
  row:            { display: "flex", alignItems: "center", gap: 10, fontWeight: 700, color: T.black },
  sub:            { paddingLeft: 22, paddingTop: 8 },
  productGroup:   { paddingLeft: 20, paddingTop: 7, paddingBottom: 4, fontWeight: 600, color: T.mutedDim, cursor: "pointer", fontSize: 13, display: "flex", alignItems: "center", gap: 8 },
  sidebar:        { background: "rgba(255,255,255,.98)", borderRadius: 16, padding: 20, boxShadow: `0 4px 24px ${RGBA_DARK(0.08)}`, position: "sticky" as const, top: 86, maxHeight: "calc(100vh - 100px)", overflowY: "auto" as const, border: T.outline },
};

/* page layout styles */
const S: Record<string, React.CSSProperties> = {
  page:        { minHeight: "100vh", background: T.pageBg, paddingBottom: 90, fontFamily: "'Inter',sans-serif", position: "relative" as const },
  content:     { position: "relative" as const, zIndex: 1 },
  headerTop:   { background: T.headerBg, backgroundImage: T.grad, position: "sticky", top: 0, zIndex: 200, boxShadow: `0 4px 24px ${RGBA_DARK(0.35)}` },
  headerRow:   { display: "flex", alignItems: "center", gap: 10, padding: "14px 16px" },
  searchWrap:  { display: "flex", alignItems: "center", flex: 1, background: "rgba(255,255,255,.10)", borderRadius: 8, overflow: "hidden", border: "1.5px solid rgba(255,255,255,.22)", transition: "border-color .2s,box-shadow .2s" },
  searchInput: { flex: 1, border: "none", outline: "none", padding: "10px 16px", fontSize: 14, color: "#fff", background: "transparent", fontFamily: "inherit" },
  searchBtn:   { width: 44, minHeight: 42, background: "rgba(255,255,255,.16)", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, borderRadius: "0 8px 8px 0" },
  desktopGrid: { display: "grid", gap: 24, padding: "24px 24px 0" },
  grid:        { display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(240px,1fr))", gap: 20 },
  mobileGrid:  { display: "grid", gridTemplateColumns: "repeat(2,1fr)", gap: 12 },
  mobileTabs:  { position: "fixed" as const, bottom: 0, left: 0, right: 0, height: 64, background: "#fff", display: "flex", justifyContent: "space-around", alignItems: "center", boxShadow: `0 -4px 24px ${RGBA_DARK(0.10)}`, zIndex: 100, borderTop: `1px solid ${T.border}` },
};

/* ═══════════════════════════════════════════════════════════
   PAGE WATERMARK — faded logo tiled across the cream background
═══════════════════════════════════════════════════════════ */
const PageWatermark: React.FC = () => (
  <div
    aria-hidden="true"
    style={{
      position: "fixed",
      inset: 0,
      zIndex: 0,
      pointerEvents: "none",
      backgroundImage: `url(${appLogo})`,
      backgroundRepeat: "repeat",
      backgroundSize: "170px 170px",
      opacity: 0.035,
      filter: "grayscale(1)",
    }}
  />
);

/* ═══════════════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════════════ */
const BooksHome: React.FC = () => {
  const dispatch  = useDispatch<AppDispatch>();
  const navigate  = useNavigate();
  const { rate, symbol } = useContext(CurrencyContext);

  const authUser   = useSelector((s: RootState) => s.auth.user);
  const authLoaded = useSelector((s: RootState) => s.auth.authLoaded);
  const userId     = authUser?.userId ?? "";

  const listings         = useSelector((s: RootState) => s.products.products);
  const filteredListings = useSelector((s: RootState) => (s as any).products.filteredProducts);
  const cartItems        = useSelector((s: RootState) => s.cart.items);
  const cartTotal         = cartItems.reduce((s: number, i: any) => s + i.qty, 0);
  const ratingSummaries   = useSelector((s: RootState) => (s as any).productRatings?.summaries ?? {});

  const commercePartners = useSelector((s: RootState) => s.commercePartners.partners);

  /* ═══════════════════════════════════════════════════════════
     BOOKS-ONLY PUBLISHER SET — businessType id 12 AND storeType
     id 1222. This is the strict filter: everything downstream
     (publisher rows, tier ranking, "only books publishers" even in
     Global mode) derives from this set, not from all
     commercePartners. Same pattern as Bags / Kitchen / Farm / Automotive.
  ═══════════════════════════════════════════════════════════ */
  const partners = useMemo(() => {
    return (commercePartners || []).filter(
      (p: any) => p.categoryId === BUSINESS_TYPE_ID && p.storeTypeId === STORE_TYPE_ID
    );
  }, [commercePartners]);

  const booksPublisherIds = useMemo(() => new Set(partners.map((p: any) => p.brandId)), [partners]);

  const publisherMap = useMemo(() => {
    const m = new Map<string, any>();
    partners.forEach((p: any) => m.set(p.brandId, p));
    return m;
  }, [partners]);

  const [isMobile,        setIsMobile]        = useState(window.innerWidth <= 768);
  const [catOpen,         setCatOpen]         = useState(false);
  const [desktopCatOpen,  setDesktopCatOpen]  = useState(false);
  const [searchQuery,     setSearchQuery]     = useState("");
  const [searchFocused,   setSearchFocused]   = useState(false);

  const [activeCat,           setActiveCat]           = useState<number | null>(null);
  const [activeProductGroup,  setActiveProductGroup]  = useState<number | null>(null);

  const [openCategory,    setOpenCategory]    = useState<string | null>(null);
  const [shopCatExpanded, setShopCatExpanded] = useState(true);

  const [userCountry,       setUserCountry]       = useState<string | null>(null);
  const [userCity,          setUserCity]          = useState<string | null>(null);
  const [userLat,           setUserLat]           = useState<number | null>(null);
  const [userLng,           setUserLng]           = useState<number | null>(null);
  const [locationDenied,    setLocationDenied]    = useState(false);
  const [locationLoading,   setLocationLoading]   = useState(true);
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [showGlobal,        setShowGlobal]        = useState(false);
  const locationActive = !locationDenied && userLat !== null;

  const [format, setFormat] = useState<number | null>(null);
  const [qty,    setQty]    = useState(1);
  const [variantOpen, setVariantOpen] = useState(false);
  const [variantItem, setVariantItem] = useState<any | null>(null);

  const [wished, setWished] = useState<Set<string>>(new Set());

  const publisherTier = useMemo(() => {
    const tiers = new Map<string, number>();
    const deviceCity = userCity?.toLowerCase().trim();
    const deviceCountry = userCountry?.toLowerCase().trim();
    const deviceContinent = getContinent(userCountry);
    partners.forEach((p: any) => {
      const pubCity = p.address?.city?.toLowerCase().trim();
      const pubCountry = p.address?.country?.toLowerCase().trim();
      const pubContinent = getContinent(p.address?.country);
      let tier = 3;
      if (deviceCity && pubCity && pubCity === deviceCity) tier = 0;
      else if (deviceCountry && pubCountry && pubCountry === deviceCountry) tier = 1;
      else if (deviceContinent && pubContinent && pubContinent === deviceContinent) tier = 2;
      tiers.set(p.brandId, tier);
    });
    return tiers;
  }, [partners, userCity, userCountry]);

  /* ── responsive ── */
  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth <= 768);
    fn(); window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);

  /* ── auth gate ── */
  useEffect(() => {
    if (!authLoaded) return;
    if (!authUser || !userId) navigate("/AuthSwitch");
  }, [authLoaded, authUser, userId, navigate]);

  /* ── data listeners/slices ── */
  useEffect(() => { dispatch(listenToPublishedProducts()); }, [dispatch]);
  useEffect(() => { dispatch(fetchAllProductSummaries()); }, [dispatch]);
  useEffect(() => {
    const unsub = dispatch(listenToBrandsByBusinessType(BUSINESS_TYPE_ID) as any);
    return () => { if (typeof unsub === "function") unsub(); };
  }, [dispatch]);
  useEffect(() => { dispatch(filterLocalProductsByCountry(null) as any); }, [dispatch]);

  /* ── geolocation ── */
  useEffect(() => {
    if (!navigator.geolocation) {
      setLocationDenied(true); setLocationLoading(false);
      dispatch(filterLocalProductsByCountry(null) as any);
      return;
    }
    const detect = () => {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setUserLat(pos.coords.latitude); setUserLng(pos.coords.longitude); setLocationDenied(false);
          if ((window as any).google?.maps) {
            const gc = new (window as any).google.maps.Geocoder();
            gc.geocode({ location: { lat: pos.coords.latitude, lng: pos.coords.longitude } }, (results: any, status: string) => {
              if (status === "OK" && results?.[0]) {
                const comps = results[0].address_components;
                const get   = (t: string) => comps.find((c: any) => c.types.includes(t))?.long_name ?? null;
                setUserCity(get("locality") || get("administrative_area_level_2"));
                const country = get("country"); setUserCountry(country);
                if (country) dispatch(filterLocalProductsByCountry(country) as any);
              }
              setLocationLoading(false);
            });
          } else { setLocationLoading(false); }
        },
        (err) => {
          if (err.code === err.PERMISSION_DENIED) setLocationDenied(true);
          setLocationLoading(false); dispatch(filterLocalProductsByCountry(null) as any);
        }
      );
    };
    detect();
    if ("permissions" in navigator) {
      navigator.permissions.query({ name: "geolocation" as PermissionName }).then(r => {
        r.onchange = () => { if (r.state === "granted") detect(); };
      });
    }
  }, [dispatch]);

  useEffect(() => {
    if (!userCountry) return;
    dispatch(filterLocalProductsByCountry(showGlobal ? null : userCountry) as any);
  }, [userCountry, showGlobal, dispatch]);

  const handleToggleGlobal = () => {
    const next = !showGlobal; setShowGlobal(next);
    dispatch(filterLocalProductsByCountry(next ? null : userCountry) as any);
  };

  useEffect(() => { if (searchQuery.trim()) setActiveProductGroup(null); }, [searchQuery]);

  const closePanel = () => { setCatOpen(false); setDesktopCatOpen(false); };

  const resetAllFilters = () => {
    setActiveCat(null); setActiveProductGroup(null);
    dispatch(setFilters({ city: null, country: null }));
    dispatch(filterLocalProductsByCountry(showGlobal ? null : userCountry) as any);
  };

  /* Top-level category tap → FILTERS in place (toggle), never navigates.
     Same behaviour as Bags / Kitchen / Farm / Automotive's handleCatSelect. */
  const handleCatSelect = (catId: number) => {
    setActiveCat(prev => {
      const next = prev === catId ? null : catId;
      if (next === null) setActiveProductGroup(null);
      return next;
    });
  };

  const handleProductGroupSelect = (catId: number, groupId: number) => {
    setActiveCat(catId);
    setActiveProductGroup(prev => (prev === groupId ? null : groupId));
    closePanel();
  };

  /* ── cart — single-publisher cart, same actions as Bags ── */
  const getQty = (id: string) => cartItems.find((i: any) => i.listingId === id)?.qty ?? 0;

  const SINGLE_PUBLISHER_CART_MODE = true;
  const ensureCartPublisherCompat = (newPublisherId?: string) => {
    if (!SINGLE_PUBLISHER_CART_MODE || !cartItems.length) return;
    const currentPublisherId = cartItems[0]?.brandId;
    if (currentPublisherId && newPublisherId && currentPublisherId !== newPublisherId) dispatch(clearCart());
  };

  const getVariantMode = (item: any): "format" | null => {
    if (!item || !item.variants || !Object.keys(item.variants).length) return null;
    return "format";
  };
  const canContinue = getVariantMode(variantItem) === null || (format !== null && qty > 0);

  const openProductModal = (item: any) => {
    setVariantItem(item); setFormat(null); setQty(1); setVariantOpen(true);
  };

  const handleAddToCart = (item: any, e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (getVariantMode(item) !== null) { openProductModal(item); return; }
    ensureCartPublisherCompat(item.brandId);
    dispatch(addToCart({
      listingId: item.listingId, name: item.name!, rate: item.rate!,
      photo: item.photos?.[0], hostId: item.hostId, brandId: item.brandId,
    } as any));
  };

  const handleVariantContinue = (toCheckout: boolean) => {
    if (!variantItem) return;
    ensureCartPublisherCompat(variantItem.brandId);
    dispatch(addToCart({
      listingId: variantItem.listingId, name: variantItem.name!,
      rate: variantItem.rate!, qty,
      photo: variantItem.variants?.[format!]?.imageUrl ?? variantItem.photos?.[0],
      formatId: format ?? undefined,
      hostId: variantItem.hostId, brandId: variantItem.brandId,
    } as any));
    setVariantOpen(false); setVariantItem(null); setFormat(null); setQty(1);
    if (toCheckout) goToCheckout();
  };

  const toggleWish = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setWished(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };

  const goToDetails  = (id: string) => navigate(`/glamourdetails/${id}`);
  const goToCheckout = () => navigate(`/glamourcheckout/${userId}`, { state: { items: cartItems } });

  /* ── filtered products — category/group + search + STRICT books-
     publisher scoping. booksPublisherIds is derived from businessType id 12
     / storeType id 1222 partners, so even in Global mode only
     Books Store publishers can ever appear here. ── */
  const filteredProducts = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    let list: any[] = (filteredListings ?? listings).filter((l: any) => {
      if (l.isPublished === false) return false;
      if (!booksPublisherIds.has(l.brandId)) return false;
      if (activeCat) {
        const matchesCat = l.categoryId === activeCat || l.subcategoryId === activeCat || l.productId === activeCat;
        if (!matchesCat) return false;
      }
      if (activeProductGroup && l.subcategoryId !== activeProductGroup && l.productId !== activeProductGroup && l.productVariantId !== activeProductGroup) return false;
      if (q && !`${l.name ?? ""} ${l.description ?? ""}`.toLowerCase().includes(q)) return false;
      return true;
    });

    if (locationActive) {
      list = list.filter((l: any) => {
        const tier = publisherTier.get(l.brandId) ?? 3;
        return showGlobal ? isGlobalTier(tier) : isLocalTier(tier);
      });
    }
    return list;
  }, [filteredListings, listings, booksPublisherIds, activeCat, activeProductGroup, searchQuery, showGlobal, publisherTier, locationActive]);

  const productsByPublisher = useMemo(() => {
    const order: string[] = [];
    const map = new Map<string, any[]>();
    filteredProducts.forEach((item: any) => {
      const bId = item.brandId || "unknown";
      if (!map.has(bId)) { map.set(bId, []); order.push(bId); }
      map.get(bId)!.push(item);
    });
    let groups = order.map(bId => ({ publisherId: bId, items: map.get(bId)! }));
    groups = [...groups].sort((a, b) => (publisherTier.get(a.publisherId) ?? 3) - (publisherTier.get(b.publisherId) ?? 3));
    return groups;
  }, [filteredProducts, publisherTier]);

  const activeCatName = activeCat ? ALL_TOP_CATS.find(c => c.id === activeCat)?.name ?? null : null;

  /* ═══════════════════════════════════
     HEADER — location · search · categories · cart. Nothing else.
  ═══════════════════════════════════ */
  const HeaderRow = () => (
    <div style={S.headerRow}>
      {locationLoading ? (
        <div className="h-btn">
          <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#fff", animation: "mc-pulse 1.2s infinite" }} />
          <span style={{ fontSize: 11, color: "#fff" }}>Locating…</span>
        </div>
      ) : locationDenied ? (
        <button className="h-btn" onClick={() => setLocationModalOpen(true)}><AlertTriangle size={14} /></button>
      ) : (
        <button onClick={handleToggleGlobal} className="h-btn" style={{ background: showGlobal ? "rgba(255,255,255,.06)" : "rgba(255,255,255,.16)" }}>
          {showGlobal ? <Globe size={13} /> : <MapPin size={13} />}
          <span style={{ display: isMobile ? "none" : "inline" }}>{showGlobal ? "Global" : userCity || userCountry || "Nearby"}</span>
        </button>
      )}

      <div style={{ ...S.searchWrap, borderColor: searchFocused ? T.orange : "rgba(255,255,255,.22)" }}>
        <input
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          onFocus={() => setSearchFocused(true)}
          onBlur={() => setSearchFocused(false)}
          placeholder="Search by title or author…"
          style={S.searchInput}
        />
        {searchQuery && (
          <button onClick={() => setSearchQuery("")} style={{ background: "none", border: "none", cursor: "pointer", padding: "0 8px", color: "#fff" }}>
            <X size={14} />
          </button>
        )}
        <button style={S.searchBtn}><Search size={17} color="#fff" /></button>
      </div>

      <button onClick={() => isMobile ? setCatOpen(true) : setDesktopCatOpen(v => !v)} className="h-btn">
        <Menu size={14} /><span style={{ display: isMobile ? "none" : "inline" }}>Categories</span>
      </button>

      <button onClick={goToCheckout} className="h-btn" style={{ position: "relative", background: cartTotal > 0 ? "rgba(212,132,60,.28)" : "rgba(255,255,255,.12)" }}>
        <ShoppingCart size={18} />
        {!isMobile && <span>{cartTotal > 0 ? `${cartTotal} items` : "Cart"}</span>}
        {cartTotal > 0 && (
          <span style={{ position: "absolute", top: -4, right: -4, background: T.red, color: "#fff", fontSize: 9, fontWeight: 900, width: 17, height: 17, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}>
            {cartTotal > 99 ? "99+" : cartTotal}
          </span>
        )}
      </button>
    </div>
  );

  /* ═══════════════════════════════════════════════════════════
     SIDEBAR TREE — SINGLE top-level expandable category cards,
     each expanding to its own subcategory list. Tapping a
     subcategory FILTERS (never navigates).
  ═══════════════════════════════════════════════════════════ */
  const CategoryTree: React.FC<{ category: CategoryItem }> = ({ category }) => {
    const isOpen = openCategory === category.name;
    const subcats = CAT_SUBCATS[category.id] ?? [];
    const isCatSelected = activeCat === category.id && activeProductGroup === null;
    return (
      <div style={A.commerceCard}>
        <div style={{ ...A.commerceHeader, color: isCatSelected ? T.red : T.black }} onClick={() => setOpenCategory(isOpen ? null : category.name)}>
          <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {category.name}
            {isCatSelected && <Check size={14} color={T.red} />}
          </span>
          <motion.div animate={{ rotate: isOpen ? 90 : 0 }}><ChevronRight size={16} /></motion.div>
        </div>
        <AnimatePresence>
          {isOpen && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}>
              <div style={A.sub}>
                {subcats.map((p) => {
                  const sel = activeProductGroup === p.id;
                  return (
                    <div key={p.id}
                      style={{ ...A.productGroup, color: sel ? T.red : T.mutedDim, fontWeight: sel ? 800 : 600 }}
                      onClick={() => handleProductGroupSelect(category.id, p.id)}>
                      {p.name}
                      {sel && <Check size={12} color={T.red} style={{ marginLeft: "auto" }} />}
                    </div>
                  );
                })}
                {subcats.length === 0 && (
                  <div style={{ ...A.productGroup, color: "#C6BBA2", cursor: "default" }}>No items yet</div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };

  const CategoryPanelContent = () => (
    <>
      {ALL_TOP_CATS.map((cat) => <CategoryTree key={cat.id} category={cat} />)}
    </>
  );

  const MobileCategorySheet = () => (
    <AnimatePresence>
      {isMobile && catOpen && (
        <>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setCatOpen(false)} style={{ position: "fixed", inset: 0, background: RGBA_DARK(0.5), zIndex: 400 }} />
          <motion.div initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }} transition={{ type: "spring", stiffness: 340, damping: 30 }}
            style={{ position: "fixed", bottom: 0, left: 0, right: 0, maxHeight: "82vh", borderRadius: "18px 18px 0 0", background: "#fff", zIndex: 401, border: T.outline, overflowY: "auto" }}>
            <div style={{ width: 36, height: 4, background: RGBA_DARK(0.16), borderRadius: 2, margin: "14px auto 0" }} />
            <div style={{ display: "flex", alignItems: "center", borderBottom: `1px solid ${T.border}`, padding: "12px 20px 10px" }}>
              <span className="tag-label" style={{ fontSize: 15, color: T.red }}>CATEGORIES</span>
              <div style={{ flex: 1 }} />
              <button onClick={() => setCatOpen(false)} style={{ background: "none", border: "none", cursor: "pointer" }}><X size={15} color={T.muted} /></button>
            </div>
            <div style={{ padding: "16px 16px 32px" }}><CategoryPanelContent /></div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );

  /* ═══════════════════════════════════
     CATEGORY ICON BAR — kept exactly as-is (scrollable, book-
     cover styled), now wired to the in-place filter instead of
     navigation.
  ═══════════════════════════════════ */
  const CategoryIconBar = () => (
    <div style={{ marginBottom: 22, padding: isMobile ? "14px 16px" : "18px 24px", background: T.headerBg, backgroundImage: T.grad, borderRadius: 14, boxShadow: `0 8px 24px ${RGBA_DARK(0.28)}` }}>
      <div style={{ display: "flex", gap: isMobile ? 14 : 22, overflowX: "auto", scrollbarWidth: "none" as any, WebkitOverflowScrolling: "touch" as any, paddingBottom: 2 }}>
        {ALL_TOP_CATS.map((cat, i) => {
          const isSel = activeCat === cat.id;
          const color = [T.blue, T.orange, T.red][i % 3];
          return (
            <button key={cat.id}
              onClick={() => handleCatSelect(cat.id)}
              style={{ background: "none", border: "none", cursor: "pointer", flexShrink: 0, display: "flex", flexDirection: "column", alignItems: "center", gap: 6, padding: "2px 4px" }}>
              <div style={{
                width: isMobile ? 46 : 54, height: isMobile ? 46 : 54, borderRadius: 10, overflow: "hidden", position: "relative",
                border: `2px solid ${isSel ? T.orange : "rgba(255,255,255,.30)"}`,
                boxShadow: isSel ? `0 0 0 3px rgba(212,132,60,.25)` : "none", transition: "all .2s",
              }}>
                {cat.image ? (
                  <img src={cat.image} alt={cat.name} style={{ width: "100%", height: "100%", objectFit: "cover", opacity: isSel ? 1 : 0.85, transition: "opacity .2s" }} />
                ) : (
                  <div style={{ width: "100%", height: "100%", background: color, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <span style={{ color: "#fff", display: "flex" }}>{cat.icon}</span>
                  </div>
                )}
              </div>
              <span style={{ fontSize: 10, fontWeight: isSel ? 800 : 600, color: "#fff", whiteSpace: "nowrap" }}>{cat.name}</span>
            </button>
          );
        })}
      </div>
    </div>
  );

  /* ═══════════════════════════════════
     SHOP BY CATEGORY — group cards, strictly idBooksCategories,
     tiles SELECT a filter (handleCatSelect), no navigation.
  ═══════════════════════════════════ */
  const ShopByCategory = () => {
    const chunk = (arr: CategoryItem[], title: string) => {
      const chunks: CategoryItem[][] = [];
      for (let i = 0; i < arr.length; i += 4) chunks.push(arr.slice(i, i + 4));
      return chunks.map((items, idx) => ({
        title: chunks.length > 1 ? `${title} ${idx + 1}` : title,
        items,
      }));
    };

    const cardGroups = chunk(ALL_TOP_CATS, "Browse by Genre");

    return (
      <div style={{ marginBottom: 28 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14, gap: 12 }}>
          <div onClick={() => setShopCatExpanded(v => !v)} style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", userSelect: "none" }}>
            <span style={{ fontWeight: 900, fontSize: isMobile ? 16 : 18, color: T.black, letterSpacing: -0.3 }}>
              Browse by Genre
            </span>
            <motion.div animate={{ rotate: shopCatExpanded ? 0 : -90 }} transition={{ duration: 0.2 }}>
              <ChevronDown size={20} color={T.muted} />
            </motion.div>
          </div>
        </div>

        <AnimatePresence initial={false}>
          {shopCatExpanded && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.25 }} style={{ overflow: "hidden" }}>
              <div style={isMobile
                ? { display: "flex", gap: 14, overflowX: "auto", scrollSnapType: "x mandatory", WebkitOverflowScrolling: "touch" as any, scrollbarWidth: "none" as any, paddingBottom: 4 }
                : { display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(220px,1fr))", gap: 18 }
              }>
                {cardGroups.map((group) => (
                  <div key={group.title}
                    style={{ flexShrink: 0, ...(isMobile ? { width: "78vw", scrollSnapAlign: "start" } : {}), background: "#fff", borderRadius: 16, overflow: "hidden", border: T.outline, boxShadow: `0 6px 28px ${RGBA_DARK(0.06)}`, transition: "box-shadow .22s, border-color .22s, transform .22s" }}
                    onMouseEnter={e => { const el = e.currentTarget as HTMLElement; el.style.boxShadow = `0 8px 0 ${RGBA_RED(0.26)}, 0 16px 38px ${RGBA_DARK(0.16)}`; el.style.borderColor = T.red; el.style.transform = "translateY(-4px)"; }}
                    onMouseLeave={e => { const el = e.currentTarget as HTMLElement; el.style.boxShadow = `0 6px 28px ${RGBA_DARK(0.06)}`; el.style.borderColor = T.border; el.style.transform = "translateY(0)"; }}
                  >
                    <div style={{ padding: isMobile ? "10px 12px 6px" : "14px 16px 8px", fontWeight: 900, fontSize: isMobile ? 12 : 14, color: T.black, letterSpacing: -.2 }}>{group.title}</div>
                    <div className="perforation" style={{ margin: "0 12px", background: `repeating-linear-gradient(90deg, ${RGBA_DARK(0.16)} 0 6px, transparent 6px 12px)`, height: 1 }} />
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: isMobile ? 4 : 8, padding: isMobile ? "8px 8px 8px" : "10px 10px 10px" }}>
                      {group.items.map((cat) => {
                        const sel = activeCat === cat.id;
                        return (
                          <button key={cat.id} onClick={() => handleCatSelect(cat.id)} style={{ background: "none", border: "none", cursor: "pointer", padding: 0, borderRadius: isMobile ? 10 : 12, overflow: "hidden" }}>
                            <div style={{ width: "100%", aspectRatio: "1/1", borderRadius: isMobile ? 10 : 12, overflow: "hidden", position: "relative", background: T.cream, outline: sel ? `2.5px solid ${T.red}` : "none", outlineOffset: 2 }}>
                              {cat.image ? (
                                <>
                                  <img src={cat.image} alt={cat.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                  <div style={{ position: "absolute", inset: 0, background: `linear-gradient(to top,${RGBA_DARK(0.75)},transparent 50%)` }} />
                                  <span style={{ position: "absolute", bottom: isMobile ? 4 : 6, left: 0, right: 0, textAlign: "center", fontSize: isMobile ? 9 : 11, fontWeight: 800, color: "#fff", lineHeight: 1.2, textShadow: "0 1px 4px rgba(0,0,0,.5)" }}>
                                    {cat.name}
                                  </span>
                                </>
                              ) : (
                                <div style={{ width: "100%", height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 6 }}>
                                  <span style={{ fontSize: 26, color: T.mutedDim, display: "flex" }}>{cat.icon}</span>
                                  <span style={{ fontSize: isMobile ? 9 : 11, fontWeight: 800, color: T.black, textAlign: "center", padding: "0 4px" }}>{cat.name}</span>
                                </div>
                              )}
                              {sel && (
                                <div style={{ position: "absolute", top: 5, right: 5, width: 18, height: 18, borderRadius: "50%", background: T.red, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                  <Check size={11} color="#fff" strokeWidth={3} />
                                </div>
                              )}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                    <button onClick={() => handleCatSelect(group.items[0].id)}
                      style={{ width: "100%", padding: isMobile ? "7px 12px" : "9px 16px", background: "none", border: "none", borderTop: `1.5px solid ${T.border}`, cursor: "pointer", fontSize: isMobile ? 11 : 13, fontWeight: 700, color: T.red, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      Explore<ArrowRight size={isMobile ? 12 : 14} />
                    </button>
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };

  /* ═══════════════════════════════════
     PUBLISHER ROW HEADER — clicking navigates to the store via publisherId
  ═══════════════════════════════════ */
  const PublisherRowHeader = ({ publisherId }: { publisherId: string }) => {
    const partner  = publisherMap.get(publisherId);
    const publisherName = partner?.name || "Publisher";
    const publisherLogo = partner?.image || appLogo;
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "26px 0 12px" }}>
        <button onClick={() => navigate(`/StoreHome/${publisherId}`)} style={{ background: "none", cursor: "pointer", width: 34, height: 34, borderRadius: 8, overflow: "hidden", flexShrink: 0, border: `1px solid ${T.border}`, padding: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <img src={publisherLogo} alt={publisherName} style={{ width: "100%", height: "100%", objectFit: "cover" }} onError={e => { (e.currentTarget as HTMLImageElement).src = appLogo; }} />
        </button>
        <span style={{ fontWeight: 900, fontSize: 14, color: T.black, textDecoration: "underline", textDecorationColor: T.orange, textDecorationThickness: 2, textUnderlineOffset: 4 }}>{publisherName}</span>
        <div style={{ flex: 1 }} />
        <motion.button whileHover={{ scale: 1.08 }} whileTap={{ scale: 0.92 }} onClick={() => navigate(`/StoreHome/${publisherId}`)}
          style={{ width: 30, height: 30, borderRadius: 8, border: "none", background: "#fff", boxShadow: `0 3px 10px ${RGBA_DARK(0.14)}`, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0 }}>
          <ChevronRight size={16} color={T.red} />
        </motion.button>
      </div>
    );
  };

  /* ═══════════════════════════════════
     BOOK CARD (unchanged UI)
  ═══════════════════════════════════ */
  const BookCard = ({ item, isMob }: { item: any; isMob: boolean }) => {
    const q         = getQty(item.listingId);
    const price     = `${symbol ?? "€"}${((item.rate ?? 0) * (rate ?? 1)).toFixed(2)}`;
    const discount  = item.discount ?? (item.compareAtRate && item.compareAtRate > (item.rate ?? 0)
      ? Math.round(((item.compareAtRate - (item.rate ?? 0)) / item.compareAtRate) * 100) : null);
    const summary   = ratingSummaries[item.listingId];
    const hasRating = summary && summary.totalReviews > 0;
    const accent    = productAccentColor(item.listingId ?? item.name ?? "x");
    const isWished  = wished.has(item.listingId);

    const overlap  = isMob ? 58 : 100;
    const imgZoneH = isMob ? 240 : 320;

    return (
      <motion.div className="p-card" whileTap={{ scale: 0.96 }}
        style={{ borderRadius: 16, overflow: "visible", background: "#fff", boxShadow: `0 1px 2px ${RGBA_DARK(0.05)}, 0 10px 28px ${RGBA_DARK(0.08)}`, cursor: "pointer", display: "flex", flexDirection: "column", height: "100%", marginTop: overlap }}
        onClick={() => goToDetails(item.listingId)}>
        <div style={{ width: "100%", height: imgZoneH, marginTop: -overlap, position: "relative", zIndex: 4, overflow: "visible", display: "flex", alignItems: "center", justifyContent: "center" }}>
          {item.photos?.[0] ? (
            <div style={{ width: "85%", height: "110%", transform: "translateY(-2%)" }}>
              <motion.img src={item.photos[0]} alt={item.name} whileHover={{ scale: 1.06 }} transition={{ type: "spring", stiffness: 300, damping: 24 }}
                style={{ width: "100%", height: "100%", objectFit: "contain", objectPosition: "center", display: "block", filter: `drop-shadow(0 16px 20px ${RGBA_DARK(0.25)})` }} />
            </div>
          ) : (
            <div style={{ width: "60%", height: "70%", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <BookOpen size={isMob ? 40 : 50} color={T.blue} style={{ opacity: 0.4 }} />
            </div>
          )}

          <button onClick={e => toggleWish(item.listingId, e)}
            style={{ position: "absolute", top: overlap - 4, right: 4, background: "rgba(255,255,255,.92)", border: "none", width: 30, height: 30, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", boxShadow: `0 2px 8px ${RGBA_DARK(0.14)}`, zIndex: 6 }}>
            <Heart size={13} color={isWished ? T.red : T.muted} fill={isWished ? T.red : "none"} />
          </button>

          {discount && (
            <div style={{ position: "absolute", top: overlap + 8, left: 4, background: T.red, color: "#fff", fontSize: 9, fontWeight: 800, padding: "2px 8px", borderRadius: 6, zIndex: 5 }}>{discount}% OFF</div>
          )}
          {item.isNewArrival && !discount && (
            <div style={{ position: "absolute", top: overlap + 8, left: 4, background: T.blue, color: "#fff", fontSize: 9, fontWeight: 800, padding: "2px 8px", borderRadius: 6, zIndex: 5 }}>NEW</div>
          )}
          {hasRating && (
            <div style={{ position: "absolute", top: overlap + (discount || item.isNewArrival ? 30 : 8), left: 4, zIndex: 6, background: "rgba(255,255,255,.92)", border: `1px solid ${T.border}`, borderRadius: 8, padding: isMob ? "3px 7px" : "4px 9px", display: "flex", alignItems: "center", gap: 3 }}>
              <Star size={isMob ? 9 : 10} fill={T.orange} color={T.orange} />
              <span style={{ color: T.black, fontWeight: 800, fontSize: isMob ? 9 : 10 }}>{summary!.avgRating.toFixed(1)}</span>
              <span style={{ color: T.muted, fontSize: isMob ? 8 : 9 }}>({summary!.totalReviews.toLocaleString()})</span>
            </div>
          )}
        </div>

        <div style={{ padding: isMob ? "2px 12px 12px" : "0px 16px 16px", display: "flex", flexDirection: "column", gap: isMob ? 2 : 3, flex: 1, justifyContent: "flex-end" }}>
          <div style={{ fontWeight: 800, fontSize: isMob ? 13 : 15, color: T.black, lineHeight: 1.25, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" as any, overflow: "hidden", minHeight: isMob ? 19 : 24 }}>
            {item.name}
          </div>
          <span style={{
            display: "inline-flex", alignItems: "center", alignSelf: "flex-start",
            background: T.gradH, backgroundSize: "200% 100%",
            animation: "gradSpin 3s ease infinite", borderRadius: 8, padding: "3px 12px",
            fontWeight: 900, color: "#fff", letterSpacing: "-0.4px",
            boxShadow: `0 3px 12px ${RGBA_RED(0.32)}`, fontSize: isMob ? 14 : 17,
          }}>{price}</span>

          {q > 0 ? (
            <motion.div initial={{ scale: 0.92, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} onClick={e => e.stopPropagation()} className="add-btn-3d"
              style={{ width: "100%", height: isMob ? 40 : 46, boxSizing: "border-box", display: "flex", alignItems: "center", justifyContent: "space-between", background: "#fff", border: `2px solid ${accent}`, borderRadius: 10, padding: "0 6px", ["--shadow" as any]: `${accent}45`, boxShadow: `0 4px 14px var(--shadow)`, marginTop: isMob ? 2 : 4 }}>
              <button style={{ width: isMob ? 26 : 30, height: isMob ? 26 : 30, borderRadius: 6, border: "none", background: accent, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}
                onClick={e => { e.stopPropagation(); dispatch(decrementCartItem({ listingId: item.listingId })); }}>
                <Minus size={isMob ? 12 : 14} color="#fff" />
              </button>
              <span style={{ color: accent, fontWeight: 900, fontSize: isMob ? 13 : 15 }}>{q}</span>
              <button style={{ width: isMob ? 26 : 30, height: isMob ? 26 : 30, borderRadius: 6, border: "none", background: accent, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}
                onClick={e => { e.stopPropagation(); handleAddToCart(item); }}>
                <Plus size={isMob ? 12 : 14} color="#fff" />
              </button>
            </motion.div>
          ) : (
            <motion.button className="add-btn-3d" whileTap={{ scale: 0.96 }}
              style={{ width: "100%", height: isMob ? 40 : 46, boxSizing: "border-box", borderRadius: 10, marginTop: isMob ? 2 : 4, border: `2px solid ${accent}`, padding: 0, display: "flex", alignItems: "center", justifyContent: "center", gap: 7, background: "#fff", ["--shadow" as any]: `${accent}45`, boxShadow: `0 4px 14px var(--shadow)`, cursor: "pointer" }}
              onClick={e => { e.stopPropagation(); handleAddToCart(item); }}>
              <ShoppingCart size={isMob ? 13 : 15} color={accent} />
              <span style={{ color: accent, fontWeight: 900, fontSize: isMob ? 11 : 13, letterSpacing: "0.06em" }}>ADD TO CART</span>
            </motion.button>
          )}
        </div>
      </motion.div>
    );
  };

  /* ═══════════════════════════════════
     SORRY / NO PUBLISHERS FOUND
  ═══════════════════════════════════ */
  const SorryNoPublishers = ({ categoryName }: { categoryName: string }) => (
    <div style={{ textAlign: "center", padding: 56 }}>
      <div style={{ fontSize: 40, marginBottom: 14 }}>📚</div>
      <h3 style={{ fontWeight: 900, fontSize: 22, color: T.black, marginBottom: 10 }}>Sorry, no {categoryName} books near you</h3>
      <p style={{ color: T.muted, marginBottom: 24, fontSize: 13 }}>Try another category, or switch to global mode.</p>
      <button onClick={resetAllFilters} style={{ padding: "12px 28px", borderRadius: 10, border: "none", background: GRAD_H, color: "#fff", fontWeight: 800, cursor: "pointer", fontSize: 13 }}>View All Genres</button>
    </div>
  );

  /* ═══════════════════════════════════
     BOOK ROWS — grouped by publisher, standard grid.
  ═══════════════════════════════════ */
  const renderBooks = (isMob: boolean) => (
    <div style={{ marginBottom: 28 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ width: 4, height: 22, borderRadius: 2, background: GRAD_H }} />
          <span style={{ fontWeight: 900, fontSize: 15, color: T.black }}>
            {activeCatName ?? (locationDenied ? "All Books" : showGlobal ? "Worldwide" : userCity ? `Popular in ${userCity}` : userCountry ?? "All Books")}
          </span>
          <span style={{ fontSize: 12, color: T.muted, background: RGBA_DARK(0.06), padding: "2px 9px", borderRadius: 8 }}>{filteredProducts.length}</span>
        </div>
        {locationActive && (
          <button onClick={handleToggleGlobal} style={{ display: "flex", alignItems: "center", gap: 5, background: showGlobal ? "#fff" : RGBA_DARK(0.06), borderRadius: 8, padding: "5px 13px", border: `1.5px solid ${showGlobal ? T.border : RGBA_RED(0.3)}`, fontSize: 11, fontWeight: 700, color: showGlobal ? T.mutedDim : T.red, cursor: "pointer" }}>
            {showGlobal ? <MapPin size={11} /> : <Globe size={11} />}{showGlobal ? "Show Local" : "Show Global"}
          </button>
        )}
      </div>

      {activeCatName && productsByPublisher.length === 0 ? (
        <SorryNoPublishers categoryName={activeCatName} />
      ) : filteredProducts.length > 0 ? (
        productsByPublisher.map(group => (
          <div key={group.publisherId}>
            <PublisherRowHeader publisherId={group.publisherId} />
            <div style={isMob ? S.mobileGrid : S.grid}>
              {group.items.map((item: any) => <BookCard key={item.listingId} item={item} isMob={isMob} />)}
            </div>
          </div>
        ))
      ) : (
        <div style={{ textAlign: "center", padding: 56 }}>
          <div style={{ fontSize: 40, marginBottom: 14 }}>📚</div>
          <h3 style={{ fontWeight: 900, color: T.black, marginBottom: 8 }}>{locationDenied ? "Location needed" : "No books found"}</h3>
          <p style={{ color: T.muted, marginBottom: 24, fontSize: 13 }}>{locationDenied ? "Enable location to see books available in your area." : "No books match your current filters."}</p>
          <button onClick={() => locationDenied ? setLocationModalOpen(true) : resetAllFilters()}
            style={{ padding: "12px 28px", borderRadius: 10, border: "none", background: GRAD_H, color: "#fff", fontWeight: 800, cursor: "pointer", fontSize: 13 }}>
            {locationDenied ? "Enable Location" : "View All Books"}
          </button>
        </div>
      )}
    </div>
  );

  /* ═══════════════════════════════════
     FORMAT MODAL (Hardcover, Paperback, Ebook, Audiobook)
  ═══════════════════════════════════ */
  const FormatModal = () => {
    if (!variantOpen || !variantItem) return null;
    const formatIds = Object.keys(variantItem?.variants ?? {}).map(Number);
    const previewImg = format && variantItem?.variants?.[format]?.imageUrl ? variantItem.variants[format].imageUrl : variantItem?.photos?.[0];

    const formatLabels: Record<number, string> = {
      0: "Hardcover", 1: "Paperback", 2: "Ebook", 3: "Audiobook"
    };

    const content = (
      <div style={{ background: "#fff", borderRadius: isMobile ? "20px 20px 0 0" : 18, overflow: "hidden", maxWidth: isMobile ? "100%" : 460, width: "100%", maxHeight: isMobile ? "85vh" : "80vh", display: "flex", flexDirection: "column" }}>
        <div style={{ height: 4, background: GRAD_H, backgroundSize: "200% 100%", animation: "gradSpin 4s ease infinite", flexShrink: 0 }} />
        {isMobile && <div style={{ width: 36, height: 4, borderRadius: 2, background: RGBA_DARK(0.18), margin: "12px auto 0", flexShrink: 0 }} />}

        <div style={{ overflowY: "auto", padding: "20px 20px 0" }}>
          {previewImg && (
            <div style={{ width: "100%", aspectRatio: "16/7", borderRadius: 12, overflow: "hidden", marginBottom: 16, background: T.cream }}>
              <img src={previewImg} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            </div>
          )}
          <div style={{ fontSize: 15, fontWeight: 800, color: T.black, marginBottom: 4 }}>{variantItem?.name}</div>
          <div style={{ fontSize: 20, fontWeight: 900, background: GRAD_H, backgroundSize: "200% 100%", animation: "gradSpin 3s ease infinite", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", marginBottom: 18 }}>
            {symbol ?? "€"}{((variantItem?.rate ?? 0) * (rate ?? 1)).toFixed(2)}
          </div>

          {formatIds.length > 0 && (
            <>
              <div className="tag-label" style={{ fontSize: 12, color: T.mutedDim, marginBottom: 12 }}>
                FORMAT
                {format && (() => {
                  return <span style={{ fontWeight: 600, color: T.red, letterSpacing: "0.02em", fontFamily: "'Inter',sans-serif", fontSize: 12 }}> — {formatLabels[format] || "Format"}</span>;
                })()}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(100px,1fr))", gap: 10, marginBottom: 20 }}>
                {formatIds.map(fId => {
                  const isSel = format === fId;
                  return (
                    <button key={fId} onClick={() => setFormat(fId)}
                      style={{ padding: "12px 16px", border: `1.5px solid ${isSel ? T.red : T.border}`, background: isSel ? RGBA_RED(0.10) : "#fff", color: T.black, fontSize: 12, fontWeight: 700, cursor: "pointer", borderRadius: 8, transition: "all .15s", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 4 }}>
                      <span>{formatLabels[fId] || `Format ${fId}`}</span>
                      {isSel && <span style={{ fontSize: 14 }}>✓</span>}
                    </button>
                  );
                })}
              </div>
            </>
          )}

          <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 20 }}>
            <span className="tag-label" style={{ fontSize: 12, color: T.mutedDim }}>QTY</span>
            <div style={{ display: "flex", border: `1.5px solid ${T.border}`, borderRadius: 10, overflow: "hidden", background: T.pageBg }}>
              <button onClick={() => setQty(v => Math.max(1, v - 1))} style={{ width: 40, height: 40, border: "none", background: "transparent", cursor: "pointer", fontSize: 16, color: T.red }}>−</button>
              <div style={{ width: 44, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15, fontWeight: 800, color: T.black, borderLeft: `1px solid ${T.border}`, borderRight: `1px solid ${T.border}` }}>{qty}</div>
              <button onClick={() => setQty(v => v + 1)} style={{ width: 40, height: 40, border: "none", background: "transparent", cursor: "pointer", fontSize: 16, color: T.red }}>+</button>
            </div>
          </div>
        </div>

        <div style={{ padding: "14px 20px 20px", display: "flex", gap: 10, flexShrink: 0, borderTop: `1px solid ${T.border}` }}>
          <button disabled={!canContinue} onClick={() => handleVariantContinue(false)}
            style={{ flex: 1, padding: "13px 0", borderRadius: 10, border: `1.5px solid ${T.black}`, background: "transparent", color: T.black, fontWeight: 700, fontSize: 13, cursor: canContinue ? "pointer" : "not-allowed", opacity: canContinue ? 1 : 0.45 }}>
            Add to Cart
          </button>
          <button disabled={!canContinue} onClick={() => handleVariantContinue(true)}
            style={{ flex: 1, padding: "13px 0", borderRadius: 10, border: "none", background: canContinue ? GRAD_H : "#DCD3BE", color: "#fff", fontWeight: 800, fontSize: 13, cursor: canContinue ? "pointer" : "not-allowed" }}>
            Buy Now
          </button>
        </div>
      </div>
    );

    return (
      <AnimatePresence>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          onClick={() => { setVariantOpen(false); setVariantItem(null); setFormat(null); }}
          style={{ position: "fixed", inset: 0, background: RGBA_DARK(0.55), zIndex: 500, backdropFilter: "blur(6px)" }}>
          {isMobile ? (
            <motion.div initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }} transition={{ type: "spring", stiffness: 340, damping: 30 }}
              onClick={e => e.stopPropagation()} style={{ position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 501 }}>{content}</motion.div>
          ) : (
            <div style={{ position: "fixed", inset: 0, zIndex: 501, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={e => e.stopPropagation()}>
              <motion.div initial={{ scale: 0.95, opacity: 0, y: 10 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.95, opacity: 0, y: 10 }}
                transition={{ type: "spring", damping: 26, stiffness: 340 }} style={{ width: 460, borderRadius: 18, overflow: "hidden", boxShadow: `0 32px 80px ${RGBA_DARK(0.35)}` }}>
                {content}
              </motion.div>
            </div>
          )}
        </motion.div>
      </AnimatePresence>
    );
  };

  /* ═══════════════════════════════════
     LOCATION MODAL
  ═══════════════════════════════════ */
  const LocationModal = () => (
    <AnimatePresence>
      {locationModalOpen && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setLocationModalOpen(false)}
          style={{ position: "fixed", inset: 0, background: RGBA_DARK(0.6), zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center", backdropFilter: "blur(8px)" }}>
          <motion.div initial={{ scale: 0.9, y: 40 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, y: 40 }} onClick={e => e.stopPropagation()}
            style={{ width: 320, borderRadius: 16, padding: 24, background: "#fff", boxShadow: `0 30px 80px ${RGBA_DARK(0.32)}`, textAlign: "center" }}>
            <div style={{ height: 5, borderRadius: 6, background: GRAD_H, marginBottom: 20 }} />
            <div style={{ fontSize: 32, marginBottom: 10 }}>📍</div>
            <h3 style={{ fontWeight: 900, marginBottom: 8, color: T.black }}>Location Disabled</h3>
            <p style={{ color: T.muted, margin: "0 0 22px", fontSize: 13, lineHeight: 1.6 }}>Enable location in your browser settings to see books available in your area.</p>
            <button onClick={() => setLocationModalOpen(false)} style={{ padding: "11px 28px", borderRadius: 10, border: "none", background: GRAD_H, color: "#fff", fontWeight: 700, cursor: "pointer", fontSize: 12 }}>Got it</button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  /* ═══════════════════════════════════
     MOBILE BOTTOM TABS
  ═══════════════════════════════════ */
  const BottomTabs = () => isMobile && userId ? (
    <div style={S.mobileTabs}>
      {[
        { icon: <Home size={22} />, label: "Books", active: true, action: () => navigate(`/BooksHome/${userId}`) },
        { icon: <ShoppingBasket size={22} />, label: "Stores", active: false, action: () => navigate("/Stores") },
        { label: "Cart", active: false, icon: (
            <div style={{ position: "relative" }}>
              <ShoppingCart size={22} />
              {cartItems.length > 0 && <span style={{ position: "absolute", top: -5, right: -5, background: T.red, color: "#fff", fontSize: 8, fontWeight: 900, width: 16, height: 16, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}>{cartItems.reduce((s: number, i: any) => s + i.qty, 0)}</span>}
            </div>
          ), action: goToCheckout },
        { icon: <ShoppingBag size={22} />, label: "Orders", active: false, action: () => navigate(`/glamourclientdashboard/${userId}`) },
        { icon: <User size={22} />, label: "Profile", active: false, action: () => navigate(`/profile/${userId}`) },
      ].map(t => (
        <motion.div key={t.label} whileTap={{ scale: 0.88 } as any} onClick={t.action} className={`tab-item${t.active ? " active" : ""}`} style={{ position: "relative" }}>
          {React.cloneElement(t.icon as any, { color: t.active ? T.red : "#9A8B7E" })}
          <span>{t.label}</span>
        </motion.div>
      ))}
    </div>
  ) : null;

  /* ═══════════════════════════════════
     RENDER
  ═══════════════════════════════════ */
  return (
    <div style={S.page}>
      <style>{GLOBAL_CSS}</style>
      <PageWatermark />

      <div style={S.content}>
        <div style={S.headerTop}>
          <HeaderRow />
        </div>

        <MobileCategorySheet />

        {!isMobile && (
          <div style={{ ...S.desktopGrid, gridTemplateColumns: desktopCatOpen ? "300px 1fr" : "1fr" }}>
            {desktopCatOpen && (
              <aside style={A.sidebar}>
                <CategoryPanelContent />
              </aside>
            )}
            <main style={{ minWidth: 0 }}>
              <CategoryIconBar />
              <ShopByCategory />

              {activeCat != null && (
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                  style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16, padding: "10px 14px", background: RGBA_RED(0.08), borderRadius: 10, border: `1.5px solid ${T.red}` }}>
                  <Tag size={16} color={T.red} />
                  <span style={{ fontSize: 12.5, fontWeight: 700, color: T.red, flex: 1 }}>Browsing: {activeCatName}</span>
                  <button onClick={() => { setActiveCat(null); setActiveProductGroup(null); }} style={{ background: "#fff", border: "none", borderRadius: 6, width: 24, height: 24, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <X size={12} color={T.red} />
                  </button>
                </motion.div>
              )}

              {renderBooks(false)}
            </main>
          </div>
        )}

        {isMobile && (
          <div style={{ padding: "14px 12px 0" }}>
            <CategoryIconBar />
            <div style={{ marginTop: 16 }}><ShopByCategory /></div>

            {activeCat != null && (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16, padding: "10px 14px", background: RGBA_RED(0.08), borderRadius: 10, border: `1.5px solid ${T.red}` }}>
                <Tag size={16} color={T.red} />
                <span style={{ fontSize: 12.5, fontWeight: 700, color: T.red, flex: 1 }}>Browsing: {activeCatName}</span>
                <button onClick={() => { setActiveCat(null); setActiveProductGroup(null); }} style={{ background: "#fff", border: "none", borderRadius: 6, width: 24, height: 24, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <X size={12} color={T.red} />
                </button>
              </motion.div>
            )}

            {renderBooks(true)}
          </div>
        )}

        <FormatModal />
        <LocationModal />

        <BottomTabs />
      </div>
    </div>
  );
};

export default BooksHome;